import React from "react";
import { Box, FormControl, FormLabel, Typography, Link } from "@mui/material";
import ImportContactsIcon from "@mui/icons-material/ImportContacts";
import { type TradePriceData } from "../types/tradePrice";
import { formatNumberWithComma } from "@/utils/formatNumber";

interface TradePricePopupContentProps {
  realEstateTransactionData: TradePriceData | null;
  contractPriceData: TradePriceData | null;
  districtName: string;
  municipality: string;
  prefecture: string;
  realEstateTransactionCount?: number;
  contractPriceCount?: number;
  onShowDetailList?: () => void;
}

/**
 * 取引価格データの詳細を表示する項目コンポーネント
 */
const TradePriceItem: React.FC<{ label: string; data: TradePriceData }> = ({
  label,
  data,
}) => {
  const formatNumber = (price: string): string => {
    if (isNaN(Number(price))) return price;
    const numPrice = Number(price);
    return numPrice.toLocaleString("ja-JP");
  };

  return (
    <Box sx={{ mb: 2, pb: 2, borderBottom: "1px solid #eee" }}>
      <Typography variant="body2" sx={{ fontWeight: "bold", mb: 1 }}>
        最近の{label}
      </Typography>

      {data.type && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            物件種別:
          </Typography>
          <Typography variant="caption">{data.type}</Typography>
        </Box>
      )}

      {data.period && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            取引時期:
          </Typography>
          <Typography variant="caption">{data.period}</Typography>
        </Box>
      )}

      {data.tradePrice && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            取引価格:
          </Typography>
          <Typography
            variant="caption"
            sx={{ fontWeight: "bold", color: "#d32f2f" }}
          >
            ¥{formatNumber(data.tradePrice)}
          </Typography>
        </Box>
      )}

      {data.area && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            面積:
          </Typography>
          <Typography variant="caption">{data.area}</Typography>
        </Box>
      )}

      {data.unitPrice && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            ㎡単価:
          </Typography>
          <Typography variant="caption">
            ¥{formatNumber(data.unitPrice)}/㎡
          </Typography>
        </Box>
      )}

      {data.pricePerUnit && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            坪単価:
          </Typography>
          <Typography variant="caption">
            ¥{formatNumber(data.pricePerUnit)}/坪
          </Typography>
        </Box>
      )}

      {data.floorPlan && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            間取り:
          </Typography>
          <Typography variant="caption">{data.floorPlan}</Typography>
        </Box>
      )}

      {data.buildingYear && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            建築年:
          </Typography>
          <Typography variant="caption">{data.buildingYear}</Typography>
        </Box>
      )}

      {data.structure && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            構造:
          </Typography>
          <Typography variant="caption">{data.structure}</Typography>
        </Box>
      )}

      {data.landShape && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            土地の形状:
          </Typography>
          <Typography variant="caption">{data.landShape}</Typography>
        </Box>
      )}

      {data.frontage && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            間口:
          </Typography>
          <Typography variant="caption">{data.frontage}m</Typography>
        </Box>
      )}

      {data.breadth && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            奥行:
          </Typography>
          <Typography variant="caption">{data.breadth}m</Typography>
        </Box>
      )}

      {data.cityPlanning && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            都市計画:
          </Typography>
          <Typography variant="caption">{data.cityPlanning}</Typography>
        </Box>
      )}

      {data.coverageRatio && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            建ぺい率:
          </Typography>
          <Typography variant="caption">{data.coverageRatio}</Typography>
        </Box>
      )}

      {data.floorAreaRatio && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            容積率:
          </Typography>
          <Typography variant="caption">{data.floorAreaRatio}</Typography>
        </Box>
      )}

      {data.remarks && (
        <Box sx={{ display: "flex", mb: 0.5 }}>
          <Typography variant="caption" sx={{ minWidth: 100, color: "#666" }}>
            備考:
          </Typography>
          <Typography variant="caption">{data.remarks}</Typography>
        </Box>
      )}
    </Box>
  );
};

/**
 * 取引価格（成約事例）ポップアップの内容を表示するコンポーネント
 */
export const TradePricePopupContent: React.FC<TradePricePopupContentProps> = ({
  realEstateTransactionData,
  contractPriceData,
  districtName,
  municipality,
  prefecture,
  realEstateTransactionCount = 0,
  contractPriceCount = 0,
  onShowDetailList,
}) => {
  return (
    <Box sx={{ width: "100%", maxHeight: 500, overflowY: "auto" }}>
      <FormControl fullWidth>
        <Box sx={{ pb: 0, borderBottom: "solid #1976d2" }}>
          <FormLabel sx={{ display: "flex", alignItems: "center" }}>
            <ImportContactsIcon fontSize="small" sx={{ mr: 0.5 }} />
            <Typography component="span" sx={{ fontWeight: "bold" }}>
              取引価格（成約事例）
            </Typography>
          </FormLabel>
          <Typography
            variant="body2"
            sx={{ ml: 2, color: "#666", lineHeight: 1.2 }}
          >
            {prefecture}
            {municipality}
            {districtName}
          </Typography>
          <Box
            sx={{
              ml: 2,
              mt: 1,
              mb: 1,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              {realEstateTransactionCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#666" }}
                >
                  不動産取引価格情報:{" "}
                  {formatNumberWithComma(realEstateTransactionCount)}件
                </Typography>
              )}
              {contractPriceCount > 0 && (
                <Typography
                  variant="caption"
                  sx={{ display: "block", color: "#666" }}
                >
                  成約価格情報: {formatNumberWithComma(contractPriceCount)}件
                </Typography>
              )}
            </Box>
            {(realEstateTransactionCount > 0 || contractPriceCount > 0) &&
              onShowDetailList && (
                <Link
                  component="button"
                  variant="caption"
                  onClick={onShowDetailList}
                  sx={{
                    cursor: "pointer",
                    textDecoration: "underline",
                    color: "primary.main",
                    "&:hover": {
                      textDecoration: "none",
                    },
                  }}
                >
                  詳細表示
                </Link>
              )}
          </Box>
        </Box>
        <Box>
          {realEstateTransactionData && (
            <TradePriceItem
              label="取引価格データ"
              data={realEstateTransactionData}
            />
          )}
          {contractPriceData && (
            <TradePriceItem label="成約価格データ" data={contractPriceData} />
          )}
        </Box>
      </FormControl>
    </Box>
  );
};
